import GlassCard from '@/components/GlassCard';
import useThemeColors from '@/hooks/useThemeColors';
import VideoPlayer from '@/pages/Courses/ClassDetail/Partials/VideoPlayer';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface BillingOrder {
    order_id: string;
}

interface Props {
    billing_orders?: BillingOrder[];
    billing_description?: {
        description: string | null;
        bunny_url: string | null;
    } | null;
}

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={className}
    >
        <path d="M19.05 4.91A9.82 9.82 0 0 0 12.03 2c-5.45 0-9.89 4.44-9.9 9.89a9.86 9.86 0 0 0 1.35 4.99L2 22l5.27-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.45 0 9.89-4.44 9.9-9.89a9.82 9.82 0 0 0-2.87-7.03Zm-7.02 15.25h-.01a8.24 8.24 0 0 1-4.19-1.15l-.3-.18-3.13.82.84-3.05-.2-.31a8.22 8.22 0 0 1-1.27-4.4c0-4.54 3.7-8.24 8.25-8.24a8.15 8.15 0 0 1 5.84 2.42 8.18 8.18 0 0 1 2.4 5.83c0 4.55-3.7 8.25-8.23 8.25Zm4.52-6.17c-.25-.12-1.47-.73-1.7-.81-.23-.08-.39-.12-.56.13-.16.24-.64.81-.78.97-.14.16-.28.18-.53.06-.25-.12-1.04-.38-1.99-1.22-.74-.66-1.24-1.47-1.39-1.72-.14-.24-.02-.37.11-.49.11-.11.25-.28.37-.41.12-.14.16-.24.25-.4.08-.16.04-.3-.02-.42-.06-.12-.56-1.34-.76-1.84-.2-.47-.4-.41-.56-.41h-.48c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.11.15 1.53.09.47-.07 1.47-.6 1.68-1.17.21-.57.21-1.06.14-1.17-.06-.11-.22-.18-.47-.3Z" />
    </svg>
);

const Billing: React.FC<Props> = ({ billing_orders, billing_description }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const hasVideo = Boolean(billing_description?.bunny_url);
    const hasDescription = Boolean(billing_description?.description);

    if (billing_orders === undefined) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
                <svg
                    aria-hidden="true"
                    role="status"
                    className="inline h-10 w-10 animate-spin text-gray-200"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                    ></path>
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                    ></path>
                </svg>
                <p className="text-sm text-gray-600">
                    {t('myAccount.loadingBilling')}
                </p>
            </div>
        );
    }

    if (!billing_orders.length) {
        return null;
    }

    const orderId = billing_orders[0].order_id;
    const accessUrl = `https://cpass.conquerx.com/order/${orderId}/verify/`;

    return (
        <div className="mx-auto max-w-4xl space-y-6">
            {hasVideo && (
                <GlassCard className="overflow-hidden shadow-lg">
                    <VideoPlayer
                        videoUrl={
                            billing_description?.bunny_url ?? undefined
                        }
                    />
                </GlassCard>
            )}

            <div className="grid gap-6 md:grid-cols-5">
                <GlassCard className="flex flex-col p-6 shadow-lg md:col-span-3">
                    {hasDescription && (
                        <p className="mb-5 text-gray-600 dark:text-neutral-300">
                            {billing_description?.description}
                        </p>
                    )}
                    <div className="mt-auto">
                        <a
                            href={accessUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`btn-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold focus:ring-2 focus:ring-offset-2 focus:outline-none ${colors.buttonPrimaryFocus}`}
                        >
                            {t('myAccount.billingAccess')}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="h-4 w-4"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                />
                            </svg>
                        </a>
                    </div>
                </GlassCard>

                <GlassCard className="flex flex-col p-6 shadow-lg md:col-span-2">
                    <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full ${colors.iconBg}`}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className={`h-5 w-5 ${colors.iconColor}`}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                            />
                        </svg>
                    </div>
                    <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
                        {t('myAccount.billingContact')}
                    </h3>
                    <div className="mt-3 space-y-3">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-neutral-500">
                                {t('myAccount.billingContactEurope')}
                            </p>
                            <a
                                href="tel:+34604563330"
                                className={`inline-flex items-center gap-2 text-sm font-medium text-gray-700 transition dark:text-neutral-200 hover:${colors.primary}`}
                            >
                                <WhatsAppIcon className="h-4 w-4 text-green-500" />
                                +34 604 56 33 30
                            </a>
                        </div>
                        <div className="border-t border-white/40 dark:border-white/10" />
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-neutral-500">
                                {t('myAccount.billingContactLatam')}
                            </p>
                            <a
                                href="tel:+18882511428"
                                className={`inline-flex items-center gap-2 text-sm font-medium text-gray-700 transition dark:text-neutral-200 hover:${colors.primary}`}
                            >
                                <WhatsAppIcon className="h-4 w-4 text-green-500" />
                                +1 888 251 1428
                            </a>
                        </div>
                    </div>
                </GlassCard>
            </div>
        </div>
    );
};

export default Billing;
